import { MediaUpload } from '@wordpress/block-editor';
import { Button } from '@wordpress/components';
import { store as coreStore } from '@wordpress/core-data';
import { useSelect } from '@wordpress/data';
import { useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

import { crop as cropIcon, image as imageIcon, trash } from '@wordpress/icons';
import classNames from 'classnames';

import ImageCanvas from './ImageCanvas';
import ImageCropperModal from './ImageCropperModal';
import Loading from './Loading';

/**
 * Image preview component, used to display an image with the ability to adjust the crop
 * or replace the image.
 *
 * @param {Object}                                              props                  Component props
 * @param {number}                                              props.aspectRatio      Image aspect ratio
 * @param {string}                                              props.aspectRatioLabel Aspect ratio label
 * @param {number}                                              props.imageId          Image ID, used to set the image selection in the media modal
 * @param {Crop|undefined}                                      props.crop             Crop object, if set
 * @param {string}                                              props.label            Label for the image preview
 * @param {boolean}                                             props.isCircular       Should the preview be circular
 * @param {boolean}                                             props.isOriginal       Is the image the original image
 * @param {(newCrop: Crop) => void}                             props.onAdjustCrop     Callback function for when the crop changes
 * @param {(id: number, width: number, height: number) => void} props.onImageChange    Callback function for when the image changes
 * @param {(Function)}                                          props.onImageRemove    Callback function for when the image is removed
 *
 * @return {JSX.Element}                                                                Image preview component
 */
const ImagePreview = ({
  aspectRatio,
  aspectRatioLabel,
  imageId,
  crop,
  onAdjustCrop,
  label = '',
  onImageChange,
  onImageRemove,
  isCircular,
  isOriginal,
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const image = useSelect(
    (select) => {
      if (!imageId) {
        return null;
      }

      return select(coreStore).getMedia(imageId, {
        _fields: ['id', 'source_url', 'media_details'],
      });
    },
    [imageId],
  );

  /**
   * Signal the parent component that the image has been replaced
   *
   * @param {Object} newImage Media object
   */
  const handleReplaceImage = (newImage) => {
    onImageChange(newImage.id, newImage.sizes.full.width, newImage.sizes.full.height);
  };

  return (
    <div className="mediapress-image-variation-picker-thumbnail">
      <div className="mediapress-image-variation-picker-thumbnail__info">
        <h3>{label}</h3>
        <span className="mediapress-image-variation-picker-tag">{aspectRatioLabel}</span>
      </div>
      <div
        className={classNames('mediapress-image-variation-picker-thumbnail__image', {
          'mediapress-image-variation-picker-thumbnail__image--circular': isCircular,
          horizontal: aspectRatio >= 1,
          vertical: aspectRatio < 1,
        })}
        style={{ aspectRatio: `${aspectRatio}` }}
      >
        {image ? (
          <>
            <ImageCanvas
              aspectRatio={aspectRatio}
              url={image.source_url}
              crop={crop}
              isCircular={isCircular}
            />
            <div className="mediapress-image-variation-picker-thumbnail__actions">
              <Button
                icon={cropIcon}
                isPrimary
                onClick={() => setIsCropModalOpen(true)}
                label={__('Crop', 'default')}
                showTooltip
                tooltipPosition="bottom center"
              />
              <ImageCropperModal
                isOpen={isCropModalOpen}
                onRequestClose={() => setIsCropModalOpen(false)}
                onConfirm={(newCrop) => onAdjustCrop(newCrop)}
                aspectRatio={aspectRatio}
                imageUrl={image.source_url}
                cropValue={crop}
                label={label}
                isCircular={isCircular}
              />
              <MediaUpload
                onSelect={handleReplaceImage}
                autoOpen
                render={({ open }) => (
                  <Button
                    icon={imageIcon}
                    isPrimary
                    onClick={open}
                    label={__('Replace image', 'default')}
                    showTooltip
                    tooltipPosition="bottom center"
                  />
                )}
                value={imageId}
              />
              {!isOriginal && (
                <Button
                  icon={trash}
                  isPrimary
                  onClick={onImageRemove}
                  label={__('Remove image', 'default')}
                  showTooltip
                  tooltipPosition="bottom center"
                />
              )}
            </div>
          </>
        ) : (
          <Loading aspectRatio={aspectRatio} isCircular={isCircular} />
        )}
      </div>
    </div>
  );
};

export default ImagePreview;
